import {useEffect} from 'react'
import {toAbsoluteUrl} from '../../../_gori/helpers'
import {SurveyPageLayout} from '../../pages/surveys/SurveyPageLayout'
import {useIntl, IntlShape} from 'react-intl'
import {useFormik, FormikProps} from 'formik'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import SortableListInput from './components/SortableListInput'
import SurveyService from './core/_requests'

// Main Form Values Interface (exported for shared use)
export interface BaeSurveyValues {
  email: string
  rankQuestions: string[]
  assistance: string
  communicationStyle: string[]
  shippingIssues: string[]
  shippingIssuesOther: string
  capabilities: string[]
  capabilitiesOther: string
  businessSize: string
  shippingRegions: string[]
  shippingRegionsOther: string
  languages: string
  carriers: string[]
  carriersOther: string
  futureFeatures: string
}

// Reusable checkbox group
interface QCheckboxGroupProps {
  formik: FormikProps<BaeSurveyValues>
  name: keyof BaeSurveyValues
  id: string
  help: string
  options: string[]
  otherName?: keyof BaeSurveyValues
  intl: IntlShape
  max?: number
}

const QCheckboxGroup = ({
  formik,
  name,
  id,
  help,
  options,
  otherName,
  intl,
  max,
}: QCheckboxGroupProps) => {
  const selections = Array.isArray(formik.values[name]) ? (formik.values[name] as string[]) : []

  const reachedMax = max !== undefined && selections.length >= max
  const hasOther = otherName !== undefined && selections.includes('Other')

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value, checked} = e.target
    const current = [...selections]

    if (checked && max !== undefined && current.length >= max) {
      return
    }

    const updated = checked ? [...current, value] : current.filter((v) => v !== value)

    if (!checked && value === 'Other' && otherName) {
      formik.setFieldValue(otherName, '')
    }

    formik.setFieldValue(name, updated)
  }

  return (
    <div className='card p-8 mb-4'>
      <fieldset>
        <legend className='form-label fw-bold mb-4'>
          <span
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage({id}),
            }}
          />
          <br />
          <small
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage({id: help}),
            }}
          />
        </legend>

        {options.map((label, idx) => {
          const checked = selections.includes(label)
          return (
            <div className='form-check mb-4' key={`${name}-${idx}`}>
              <input
                className='form-check-input'
                type='checkbox'
                id={`${name}-${idx}`}
                name={name}
                value={label}
                onChange={handleCheckboxChange}
                checked={checked}
                disabled={!checked && reachedMax}
              />
              <label className='form-check-label' htmlFor={`${name}-${idx}`}>
                {label}
              </label>
            </div>
          )
        })}

        {otherName && (
          <div className='form-check mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`${name}-other-checkbox`}
              name={name}
              value='Other'
              onChange={handleCheckboxChange}
              checked={hasOther}
              disabled={!hasOther && reachedMax}
            />
            <label className='form-check-label' htmlFor={`${name}-other-checkbox`}>
              {intl.formatMessage({id: 'BAE_SURVEY_OTHER'})}
            </label>
          </div>
        )}

        {hasOther && otherName && (
          <div className='form-group mt-4'>
            <input
              id={`${name}-other`}
              type='text'
              name={otherName}
              className='form-control input-other border-top-0 border-right-0 border-left-0 border-bottom bg-transparent rounded-0 py-0 px-1'
              placeholder={intl.formatMessage({
                id: 'BAE_SURVEY_OTHER_PLACEHOLDER',
              })}
              onChange={formik.handleChange}
              value={formik.values[otherName] as string}
            />
          </div>
        )}
        {formik.submitCount > 0 && formik.errors[name] && (
          <div className='text-danger'>{formik.errors[name]}</div>
        )}
      </fieldset>
    </div>
  )
}

// Main Form Component
const BaeSurvey = () => {
  const intl = useIntl()

  const initialValues: BaeSurveyValues = {
    email: '',
    rankQuestions: [],
    assistance: '',
    communicationStyle: [],
    shippingIssues: [],
    shippingIssuesOther: '',
    capabilities: [],
    capabilitiesOther: '',
    businessSize: '',
    shippingRegions: [],
    shippingRegionsOther: '',
    languages: '',
    carriers: [],
    carriersOther: '',
    futureFeatures: '',
  }

  const validationSchema = Yup.object({
    email: Yup.string().email().required('Required'),
    rankQuestions: Yup.array().min(1, 'Required'),
    assistance: Yup.string().required('Required'),
    communicationStyle: Yup.array().min(1, 'Required'),
    shippingIssues: Yup.array().min(1, 'Required').max(3, 'Select up to 3'),
    shippingIssuesOther: Yup.string().when('shippingIssues', {
      is: (vals: string[]) => vals.includes('Other'),
      then: (schema) => schema.required('Please specify'),
      otherwise: (schema) => schema.strip(),
    }),
    capabilities: Yup.array().min(1, 'Required'),
    businessSize: Yup.string().required('Required'),
    shippingRegions: Yup.array().min(1, 'Required'),
    languages: Yup.string().required('Required'),
    carriers: Yup.array().min(1, 'Required'),
    futureFeatures: Yup.string(),
  })

  const formik = useFormik<BaeSurveyValues>({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true)
      try {
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 8000)
        )
        await Promise.race([SurveyService.sendBaeSurvey(values), timeout])
        window.scrollTo({top: 0, behavior: 'smooth'})
        toast.success(intl.formatMessage({id: 'BAE_SURVEY_SUBMIT_SUCCESS'}))
        actions.resetForm()
      } catch (err: unknown) {
        if (err instanceof Error) {
          toast.error(intl.formatMessage({id: 'BAE_SURVEY_SUBMIT_FAILED'}, {message: err.message}))
          console.error('Survey submission error:', err.message)
        }
      } finally {
        actions.setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0]
      const el = document.querySelector<HTMLElement>(`[name="${firstErrorKey}"]`)
      if (el) {
        el.scrollIntoView({behavior: 'smooth', block: 'center'})
      }
    }
  }, [formik.submitCount, formik.errors])

  return (
    <>
      <SurveyPageLayout>
        {/* Banner */}
        <section className='hero p-0 mb-4'>
          <img
            className='rounded-1 w-100'
            src={toAbsoluteUrl('/media/gori/surveys/bae-survey-banner.webp')}
            alt='Your Personal AI Assistant'
          />
        </section>

        {/* Form */}
        <section className='form'>
          <form onSubmit={formik.handleSubmit}>
            {/* Email */}
            <div className='card p-8 mb-4'>
              <h1
                className='mb-4'
                dangerouslySetInnerHTML={{__html: intl.formatMessage({id: 'BAE_SURVEY_TITLE'})}}
              />
              <p
                className='mb-4'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'BAE_SURVEY_DESCRIPTION'}),
                }}
              />
              <hr className='mb-4' />
              <label
                htmlFor='email'
                className='form-label'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'BAE_SURVEY_EMAIL_LABEL'}),
                }}
              />
              <input
                id='email'
                name='email'
                type='email'
                className='form-control'
                onChange={formik.handleChange}
                value={formik.values.email}
                onBlur={formik.handleBlur}
              />
              {formik.submitCount > 0 && formik.errors.email && (
                <div className='text-danger'>{formik.errors.email}</div>
              )}
              <hr className='mb-4' />
              <p
                className='text-danger'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'BAE_SURVEY_LEGEND'}),
                }}
              />
            </div>

            {/* Q1 – Sortable */}
            <div className='card p-8 mb-4'>
              <fieldset>
                <legend className='form-label fw-bold mb-4'>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage({id: 'BAE_SURVEY_Q1_LABEL'}),
                    }}
                  />
                  <br />
                  <small
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage({id: 'BAE_SURVEY_Q1_HELP'}),
                    }}
                  />
                </legend>
                <SortableListInput
                  list={[
                    'BAE_SURVEY_Q1_OPTION_1',
                    'BAE_SURVEY_Q1_OPTION_2',
                    'BAE_SURVEY_Q1_OPTION_3',
                    'BAE_SURVEY_Q1_OPTION_4',
                    'BAE_SURVEY_Q1_OPTION_5',
                  ]}
                  name='rankQuestions'
                  formik={formik}
                />
              </fieldset>
            </div>

            {/* Q2 – Assistance */}
            <div className='card p-8 mb-4'>
              <fieldset>
                <legend className='form-label fw-bold mb-4'>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage({id: 'BAE_SURVEY_Q2_LABEL'}),
                    }}
                  />
                  <br />
                  <small
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage({id: 'BAE_SURVEY_Q2_HELP'}),
                    }}
                  />
                </legend>
                {['doItForMe', 'guideMe', 'teachMe', 'showMe'].map((val, idx) => (
                  <div className='form-check mb-4' key={`assistance-${idx}`}>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='assistance'
                      id={`assistance-option-${idx}`}
                      value={val}
                      onChange={formik.handleChange}
                      checked={formik.values.assistance === val}
                    />
                    <label className='form-check-label' htmlFor={`assistance-option-${idx}`}>
                      {intl.formatMessage({id: `BAE_SURVEY_Q2_OPTION_${idx + 1}`})}
                    </label>
                  </div>
                ))}
                {formik.submitCount > 0 && formik.errors.assistance && (
                  <div className='text-danger'>{intl.formatMessage({id: 'REQUIRED'})}</div>
                )}
              </fieldset>
            </div>

            {/* Q3 Communication Style*/}
            <QCheckboxGroup
              formik={formik}
              intl={intl}
              name='communicationStyle'
              id='BAE_SURVEY_Q3_LABEL'
              help='BAE_SURVEY_Q3_HELP'
              options={[1, 2, 3, 4, 5, 6, 7].map((n) =>
                intl.formatMessage({id: `BAE_SURVEY_Q3_OPTION_${n}`})
              )}
            />

            {/* Q4 Shipping Issues */}
            <QCheckboxGroup
              formik={formik}
              intl={intl}
              name='shippingIssues'
              id='BAE_SURVEY_Q4_LABEL'
              help='BAE_SURVEY_Q4_HELP'
              options={[...Array(11)].map((_, i) =>
                intl.formatMessage({id: `BAE_SURVEY_Q4_OPTION_${i + 1}`})
              )}
              otherName='shippingIssuesOther'
              max={3}
            />

            {/* Q5 Capabilities */}
            <QCheckboxGroup
              formik={formik}
              intl={intl}
              name='capabilities'
              id='BAE_SURVEY_Q5_LABEL'
              help='BAE_SURVEY_Q5_HELP'
              options={[...Array(9)].map((_, i) =>
                intl.formatMessage({id: `BAE_SURVEY_Q5_OPTION_${i + 1}`})
              )}
              otherName='capabilitiesOther'
            />

            {/* Q6 – Business Size */}
            <div className='card p-8 mb-4'>
              <fieldset>
                <legend
                  className='form-label fw-bold mb-4'
                  dangerouslySetInnerHTML={{
                    __html: intl.formatMessage({id: 'BAE_SURVEY_Q6_LABEL'}),
                  }}
                />
                {['under10', '10-49', '50-249', 'over250'].map((val, i) => (
                  <div className='form-check mb-2' key={`businessSize-${i}`}>
                    <input
                      className='form-check-input'
                      type='radio'
                      id={`businessSize-option-${i}`}
                      name='businessSize'
                      value={val}
                      onChange={formik.handleChange}
                      checked={formik.values.businessSize === val}
                    />
                    <label className='form-check-label' htmlFor={`businessSize-option-${i}`}>
                      {intl.formatMessage({id: `BAE_SURVEY_Q6_OPTION_${i + 1}`})}
                    </label>
                  </div>
                ))}
                {formik.submitCount > 0 && formik.errors.businessSize && (
                  <div className='text-danger'>{intl.formatMessage({id: 'REQUIRED'})}</div>
                )}
              </fieldset>
            </div>

            {/* Q7 – Regions */}
            <QCheckboxGroup
              formik={formik}
              intl={intl}
              name='shippingRegions'
              id='BAE_SURVEY_Q7_LABEL'
              help='BAE_SURVEY_Q7_HELP'
              options={[1, 2, 3, 4, 5].map((n) =>
                intl.formatMessage({id: `BAE_SURVEY_Q7_OPTION_${n}`})
              )}
              otherName='shippingRegionsOther'
            />

            {/* Q8 – Languages */}
            <div className='card p-8 mb-4'>
              <fieldset>
                <legend
                  className='form-label fw-bold mb-4'
                  dangerouslySetInnerHTML={{
                    __html: intl.formatMessage({id: 'BAE_SURVEY_Q8_LABEL'}),
                  }}
                />
                <input
                  type='text'
                  className='form-control'
                  name='languages'
                  placeholder={intl.formatMessage({id: 'BAE_SURVEY_Q8_PLACEHOLDER'})}
                  onChange={formik.handleChange}
                  value={formik.values.languages}
                />
                {formik.submitCount > 0 && formik.errors.languages && (
                  <div className='text-danger'>{intl.formatMessage({id: 'REQUIRED'})}</div>
                )}
              </fieldset>
            </div>

            {/* Q9 – Carriers */}
            <QCheckboxGroup
              formik={formik}
              intl={intl}
              name='carriers'
              id='BAE_SURVEY_Q9_LABEL'
              help='BAE_SURVEY_Q9_HELP'
              options={[1, 2, 3, 4, 5, 6, 7].map((n) =>
                intl.formatMessage({id: `BAE_SURVEY_Q9_OPTION_${n}`})
              )}
              otherName='carriersOther'
            />

            {/* Q10 – Textarea */}
            <div className='card p-8 mb-4'>
              <h3
                className='fw-bold form-label'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'BAE_SURVEY_Q10_PRELABEL'}),
                }}
              />
              <fieldset>
                <legend className='form-label fw-bold mb-4'>
                  {intl.formatMessage({id: 'BAE_SURVEY_Q10_LABEL'})}
                </legend>
                <textarea
                  className='form-control'
                  name='futureFeatures'
                  rows={2}
                  placeholder={intl.formatMessage({id: 'BAE_SURVEY_Q10_PLACEHOLDER'})}
                  onChange={formik.handleChange}
                  value={formik.values.futureFeatures}
                />
              </fieldset>
            </div>

            {/* Buttons */}
            <div className='d-flex justify-content-between gap-2'>
              <button
                type='reset'
                className='btn btn-link'
                onClick={formik.handleReset}
                dangerouslySetInnerHTML={{__html: intl.formatMessage({id: 'BAE_SURVEY_CLEAR'})}}
              />
              <button
                type='submit'
                className='btn btn-advanced-primary'
                disabled={formik.isSubmitting}
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'BAE_SURVEY_SUBMIT'}),
                }}
              />
            </div>
          </form>
        </section>
      </SurveyPageLayout>
    </>
  )
}

export {BaeSurvey}
